import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { useAdAccount } from "../lib/adAccount";
import { Bell, Warning, CheckCircle, X, ArrowsClockwise } from "@phosphor-icons/react";

const fmtMoney = (n) => "RM" + (n || 0).toLocaleString("en-MY", { maximumFractionDigits: 2 });

export default function NotificationBell() {
  const { selected, accounts } = useAdAccount();
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const dropRef = useRef(null);

  const load = () => {
    if (!selected) { setStatus(null); return; }
    setRefreshing(true);
    api.get(`/billing/status?ad_account_id=${selected}`)
      .then(r => setStatus(r.data))
      .catch(() => setStatus(null))
      .finally(() => setRefreshing(false));
  };

  useEffect(() => { load(); }, [selected]); // eslint-disable-line

  useEffect(() => {
    const onClick = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const acct = accounts.find(a => a.id === selected);
  const hasAlert = status?.configured && status.severity !== "ok";
  const count = hasAlert ? 1 : 0;

  const sevColor = (sev) => ({
    warning: "text-amber-400",
    critical: "text-orange-400",
    urgent: "text-red-400",
  }[sev] || "text-zinc-400");

  return (
    <div className="relative" ref={dropRef}>
      <button
        data-testid="notification-bell"
        onClick={() => setOpen(!open)}
        className="relative p-2 hover:bg-zinc-900 transition-colors"
      >
        <Bell size={15} weight={count ? "fill" : "regular"} className={count ? "text-orange-400" : "text-zinc-400"} />
        {count > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-orange-500 text-zinc-950 text-[9px] font-mono font-bold flex items-center justify-center">
            {count}
          </span>
        )}
      </button>
      {open && (
        <div data-testid="notifications-dropdown" className="absolute left-full ml-2 top-0 w-80 bg-[#0A0A0A] border border-zinc-800 shadow-2xl z-50">
          <div className="px-3 py-2 border-b border-zinc-800 flex items-center justify-between">
            <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-zinc-500">// Alerts · {acct?.name}</div>
            <div className="flex items-center gap-1">
              <button
                data-testid="notif-refresh"
                onClick={load}
                className="text-zinc-500 hover:text-zinc-200 p-0.5"
                title="Refresh"
              >
                <ArrowsClockwise size={11} className={refreshing ? "animate-spin" : ""} />
              </button>
              <button onClick={() => setOpen(false)} className="text-zinc-500 hover:text-zinc-200"><X size={12} /></button>
            </div>
          </div>
          <div className="max-h-80 overflow-y-auto">
            {!status && (
              <div className="px-3 py-4 text-xs text-zinc-500">Loading...</div>
            )}
            {status && !status.configured && (
              <div className="px-3 py-4 text-xs text-zinc-500">
                No billing config for this ad account. Set credit limit in <span className="text-orange-400">Settings → Billing</span>.
              </div>
            )}
            {status?.configured && (
              <div data-testid={`notification-${status.ad_account_id}`} className="px-3 py-2.5">
                <div className="flex items-start gap-2">
                  {hasAlert ? (
                    <Warning size={13} weight="fill" className={sevColor(status.severity) + " mt-0.5 shrink-0"} />
                  ) : (
                    <CheckCircle size={13} weight="fill" className="text-emerald-400 mt-0.5 shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className={`text-[11px] ${hasAlert ? sevColor(status.severity) : "text-emerald-400"} font-mono uppercase tracking-wider`}>
                      [{status.severity}] {status.pct_remaining}% remaining
                    </div>
                    <div className="text-xs text-zinc-200 mt-1">
                      {fmtMoney(status.remaining)} of {fmtMoney(status.credit_limit)}
                    </div>
                    <div className="text-[11px] text-zinc-500 mt-0.5">
                      Cycle ends {status.cycle_end}
                    </div>
                    <div className="mt-1.5 h-1 bg-zinc-800 relative overflow-hidden">
                      <div
                        className={`h-full ${status.severity === "urgent" ? "bg-red-500" : status.severity === "critical" ? "bg-orange-500" : status.severity === "warning" ? "bg-amber-500" : "bg-emerald-500"}`}
                        style={{ width: `${Math.min(100, 100 - status.pct_remaining)}%` }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
